import { Fragment, memo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import Loader from "../Others/loader";
import style from "../../Style/Component/file-picker.module.scss";

function CustomFilePickerUpload(props) {
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        // Keep local preview in sync — including when parent clears file after submit
        setFile(props.file || null);
        if (!props.file && props.uniqueId) {
            const input = document.getElementById(props.uniqueId);
            if (input) input.value = "";
        }
    }, [props.file, props.uniqueId]);

    const _handleChange = (event, is_clear) => {
        if (is_clear) {
            props.clearFile?.();
            setFile(null);
            return;
        }

        const selectedFile = event?.target?.files?.[0];
        if (!selectedFile) return;

        if (props.maxSize && selectedFile.size > props.maxSize) {
            toast.error(
                `File size exceeds the maximum limit of ${Math.round(
                    props.maxSize / 1024 / 1024
                )} MB`
            );
            props.sizeError?.();
            return;
        }

        setFile(selectedFile);
        props.onSelectFile?.(selectedFile);
        event.target.value = null;
    };

    const triggerFileInput = () => {
        document.getElementById(props.uniqueId).click();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (!droppedFile) return;

        if (props.maxSize && droppedFile.size > props.maxSize) {
            toast.error(
                `File size exceeds the maximum limit of ${Math.round(
                    props.maxSize / 1024 / 1024
                )} MB`
            );
            props.sizeError?.();
            return;
        }

        setFile(droppedFile);
        props.onSelectFile?.(droppedFile);
    };

    const isImage = file && file.type?.startsWith("image/");

    return (
        <Fragment>
            <p className="e-primary-font e-font-14 line-height-16px mb-2">
                {props.label}
                <span className="e-text-lust">{props.postfix}</span>
            </p>

            {file ? (
                <>
                    <div className="d-flex align-items-center gap-4">
                        <div
                            className={`
                                ${style.e_file}
                                ${props.customClassName}
                                ${props.error ? style.errorBorder : ''}
                            `}
                        >
                            {props.loader ? (
                                <div className={style.loaderWrapper}>
                                    <Loader classHeight="20vh" className="pt-0" />
                                </div>
                            ) : isImage ? (
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt="preview"
                                    className={style.previewImage}
                                />
                            ) : (
                                <div className={style.fileNameWrapper}>
                                    <p className="e-primary-font e-font-weight-500 e-font-14 line-height-20px e-text-charcoal mb-0 text-center">
                                        {file.name}
                                    </p>
                                </div>
                            )}
                        </div>

                        {file && !props.loader && (
                            <img
                                src={require("../../Assets/Images/Header/delete.png")}
                                width={16}
                                height={16}
                                className="cursor-pointer top-46px"
                                onClick={() => _handleChange(null, true)}
                                alt="delete"
                            />
                        )}
                    </div>

                    <div className="pt-1 d-flex justify-content-space-between">
                        <div className="e-font-10 line-height-16px e-primary-font e-text-chinese-black">
                            File supported: {props.accept || "PNG, JPG, XLSX, XLS"}
                        </div>
                        <div className="e-font-10 line-height-16px e-primary-font e-text-chinese-black">
                            Maximum file size: {Math.round(props.maxSize / 1024 / 1024)} MB
                        </div>
                    </div>
                </>
            ) : (
                <div
                    id={props.id}
                    onClick={triggerFileInput}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    className={`${style.dropZone}`}
                >
                    <div
                        className={`
                            ${style.e_file}
                            ${isDragging ? style.dragging : ""}
                            ${props.className}
                            cursor-pointer
                            d-flex align-items-center justify-content-center text-center
                            ${props.error ? style.errorBorder : ""}
                        `}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <img
                                src={require("../../Assets/Images/Home/upload.png")}
                                width={30}
                                height={30}
                                className="cursor-pointer top-46px"
                                alt="Upload"
                            />
                            <div className="text-center e-primary-font e-font-10 line-height-16px">
                                Drag & Drop <br /> or <br />
                                <span className="color-teal-green e-primary-font">Upload</span> file
                            </div>
                        </div>
                    </div>

                    <div className="pt-1 d-flex justify-content-space-between">
                        <div className="e-font-10 line-height-16px e-primary-font e-text-chinese-black">
                            File supported: {props.accept || "PNG, JPG, XLSX, XLS"}
                        </div>
                        <div className="e-font-10 line-height-16px e-primary-font e-text-chinese-black">
                            Maximum file size: {Math.round(props.maxSize / 1024 / 1024)} MB
                        </div>
                    </div>

                    {props.error && (
                        <p className="position-absolute e-error-message e-primary-font e-font-10 line-height-16px mt-1 mb-0">
                            {props.error}
                        </p>
                    )}
                </div>
            )}

            <input
                id={props.uniqueId}
                type="file"
                className="d-none"
                onChange={(e) => _handleChange(e, false)}
                accept={props.accept}
            />
        </Fragment>
    );
}

CustomFilePickerUpload.defaultProps = {
    sizeError: () => { },
    maxSize: 20 * 1024 * 1024, // 20 MB
};

export default memo(CustomFilePickerUpload);
